import { yupResolver } from "@hookform/resolvers/yup";
import { DialogContent } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ReactHookFields from "src/components/shared/ReactHookForm";
import useAppGlobal from "src/context/useAppGlobal";
import useAuthGlobal from "src/context/useAuthGlobal";
import useProviderCreate from "src/hooks/setting/provider/useProviderCreate";
import useProviderDetail from "src/hooks/setting/provider/useProviderDetail";
import useProviderUpdate from "src/hooks/setting/provider/useProviderUpdate";
import useProviderSchema from "./utils/useProviderSchema";

export default function ProviderCreate({ item }) {
  const { userMe } = useAuthGlobal();
  const { closeDialog } = useAppGlobal();
  const [currAddLevel, setCurrAddLevel] = useState("levelTwo");

  const { trigger: createProvider } = useProviderCreate();
  const { trigger: updateProvider } = useProviderUpdate(item?._id);
  const { data: providerDetail, isLoading } = useProviderDetail(item?._id);

  const { providerObject, providerFieldList, validationSchema } = useProviderSchema({
    userMe,
    currAddLevel,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: providerObject,
    shouldUnregister: false,
  });

  const formValues = watch();
  const isVietnam = ["vietnam", "việt nam"].includes(
    String(formValues?.country?.name ?? "")
      .trim()
      .toLowerCase(),
  );

  const handleCreateProvider = async (data) => {
    const formattedData = {
      name: data?.name || "",
      email: data?.email || "",
      phone: data?.phone || "",
      taxCode: data?.taxCode || "",
      detailAddress: data?.detailAddress || "",
      note: data?.note || "",
      country: data?.country?._id ?? data?.country?.value ?? data?.country ?? null,
      province: data?.province?._id ?? data?.province?.value ?? null,
      district: data?.district?._id ?? data?.district?.value ?? null,
      addressLevel: data?.addressLevel === 3 || data?.addressLevel === "levelThree" ? 3 : 2,
      ward: Number(data?.ward?._id ?? data?.ward?.value ?? data?.ward) || null,
    };

    const res = item ? await updateProvider({ body: formattedData }) : await createProvider({ body: formattedData });

    if (res?.success) {
      reset();
      closeDialog();
      enqueueSnackbar("Thành công!", { variant: "success" });
    } else {
      enqueueSnackbar(res?.error?.message || "Có lỗi xảy ra", { variant: "error" });
    }
  };

  useEffect(() => {
    if (!isVietnam && formValues?.country) {
      if (formValues?.province) setValue("province", undefined);
      if (formValues?.district) setValue("district", undefined);
      if (formValues?.ward) setValue("ward", undefined);
    } else if (!item) {
      if (!formValues?.province) {
        setValue("district", undefined);
        setValue("ward", undefined);
      } else if (!formValues?.district) {
        setValue("ward", undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.country, formValues?.province, formValues?.district, isVietnam, item]);

  useEffect(() => {
    if (!isLoading && providerDetail?.data) {
      const detail = providerDetail.data;
      const ward = detail.ward || {};
      const district = ward?.district || {};
      const province = ward?.district?.province || ward?.province || {};
      reset({
        name: detail.name || "",
        email: detail.email || "",
        phone: detail.phone || "",
        taxCode: detail.taxCode || "",
        detailAddress: detail.detailAddress || "",
        note: detail.note || "",
        country: detail.country || undefined,
        addressLevel: district?._id ? "levelThree" : "levelTwo",
        ward: ward,
        district: district,
        province: province,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, providerDetail]);

  useEffect(() => {
    if (formValues?.addressLevel) {
      setCurrAddLevel(formValues?.addressLevel);
    }
  }, [formValues?.addressLevel, setCurrAddLevel]);

  return (
    <DialogContent>
      <ReactHookFields
        pt="12px"
        onSubmit={handleSubmit(handleCreateProvider)}
        control={control}
        errors={errors}
        btnFullWidth
        isLoading={isLoading}
        listFields={providerFieldList?.map((field) => {
          const dependentValue = field?.disabledDependsOn && getValues()?.[field.disabledDependsOn];
          const isDisabled = (field?.disabledDependsOn && !dependentValue) || (!isVietnam && ["addressLevel", "province", "district", "ward"].includes(field?.name));
          const dynamicParams = { ...(field?.params ?? {}) };

          if (field?.name === "province") {
            dynamicParams.version = formValues?.addressLevel === "levelTwo" ? 2 : 3;
          } else if (field?.name === "district") {
            dynamicParams.province = formValues?.province?._id || formValues?.province?.value || "";
            dynamicParams.version = 3;
          } else if (field?.name === "ward") {
            if (formValues?.addressLevel === "levelTwo") {
              dynamicParams.province = formValues?.province?._id || formValues?.province?.value || "";
              dynamicParams.version = 2;
            } else {
              dynamicParams.district = formValues?.district?._id || formValues?.district?.value || "";
              dynamicParams.version = 3;
            }
          } else if (field?.disabledDependsOn) {
            dynamicParams[field.disabledDependsOn] = dependentValue?._id || dependentValue?.value || "";
          }

          return {
            ...field,
            disabled: isDisabled,
            required:
              field.name === "country" ||
              (isVietnam && field.name === "addressLevel") ||
              (isVietnam && ["province", "ward"].includes(field.name)) ||
              (isVietnam && formValues?.addressLevel === "levelThree" && field.name === "district"),
            params: field?.params ? dynamicParams : null,
          };
        })}
      />
    </DialogContent>
  );
}
